/* eslint-disable no-nested-ternary */
import {
  Button,
  ButtonEmpty,
  DatePicker,
  FieldNumber,
  FieldText,
  FilePicker,
  FlexGroup,
  FlexItem,
  Form,
  FormRow,
  HorizontalRule,
  Outline,
  Spacer,
  Text,
  TextArea,
  Title,
  Progress,
  notification,
} from '@antoree/ant-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { DownloadList } from 'components';
import moment from 'moment';
import { memo, useEffect, useState, useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { HomeworkFile, Session, SessionContentResponse } from 'services';
import * as yup from 'yup';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { AlertTriangleIcon } from '../../../containers/Chat/utils';

export interface IFormData {
  idParam: Number;
  link: string;
  title: string;
  vocabularyContent: string;
  studentNote: string;
  grammarContent: string;
  homeworkContent: string;
  homeworkFile: string;
  homeworkResult: string;
  bookName: string;
  bookPages: number;
  bookUnits: string;
  bookLessons: string;
}
export type AddSessionContentProps = {
  view: boolean;
  allowSubmit: boolean;
  onReload: (session: Session) => void;
  onSubmit: (formData: IFormData, isDirty: boolean) => void;
  onSendHomework: (files: FileList | null) => void;
  isLoadingSendHomeworkFile: boolean;
  isLoadingSendHomeworkVideo: boolean;
  onDeleteFile: (file: HomeworkFile) => void;
  sessionContent: SessionContentResponse | null | undefined;
  isTriggerFile: boolean;
  isNotHappened: boolean;
  isLoading: boolean;
  onSendHomeworkVideo: (videos: FileList | null) => void;
  onDeleteVideo: (videoUrlOrIndex: string | number) => void;
  isLoadingDeleteVideo?: boolean;
  isTest: boolean;
  // Thêm props cho progress bar
  uploadProgress: number;
  isUploading: boolean;
  videoInputKey: number;
  homeworkInputKey: number;
};
export const ADD_SESSION_CONTENT_FORM_ID = 'ADD_SESSION_CONTENT_FORM_ID';

const SessionContentForm: React.FC<AddSessionContentProps> = ({
  view,
  onSubmit,
  onSendHomework,
  isLoadingSendHomeworkFile,
  isLoadingSendHomeworkVideo,
  onDeleteFile,
  sessionContent,
  isTriggerFile,
  isNotHappened,
  onSendHomeworkVideo,
  onDeleteVideo,
  isLoadingDeleteVideo,
  isLoading,
  isTest,
  // Thêm props mới
  uploadProgress,
  isUploading,
  videoInputKey,
  homeworkInputKey,
}) => {
  const intl = useIntl();
  const { XIcon } = Outline;
  // Track videos that failed to load (deleted from R2)
  const [deletedVideos, setDeletedVideos] = useState<Set<string>>(new Set());
  const [checkedVideos, setCheckedVideos] = useState<Set<string>>(new Set());
  // Manual errors - only show after Save click
  const [manualErrors, setManualErrors] = useState<Record<string, string>>({});

  // Function to check if video URL returns 404
  const checkVideoExists = useCallback(
    async (videoUrl: string) => {
      // Skip if already checked
      if (checkedVideos.has(videoUrl)) {
        return;
      }

      // Only check R2 URLs (not S3)
      if (
        typeof videoUrl === 'string' &&
        (videoUrl.includes('s3.amazonaws.com') ||
          videoUrl.includes('.s3.') ||
          /https?:\/\/[^/]+\.s3[^/]*\.amazonaws\.com/.test(videoUrl))
      ) {
        setCheckedVideos(prev => new Set(prev).add(videoUrl));
        return;
      }

      try {
        const response = await fetch(videoUrl, { method: 'HEAD' });
        setCheckedVideos(prev => new Set(prev).add(videoUrl));

        // If 404, mark as deleted
        if (response.status === 404) {
          setDeletedVideos(prev => new Set(prev).add(videoUrl));
        }
      } catch (error) {
        // Network error or CORS issue - mark as checked but don't mark as deleted yet
        // Will be handled by onError handler
        setCheckedVideos(prev => new Set(prev).add(videoUrl));
      }
    },
    [checkedVideos],
  );

  // Check all video URLs when sessionContent changes
  useEffect(() => {
    if (!sessionContent?.videoUrl) return;

    let allVideos: string[] = [];

    // Handle both array and object formats
    if (Array.isArray(sessionContent.videoUrl)) {
      allVideos = sessionContent.videoUrl || [];
    } else if (
      sessionContent.videoUrl &&
      typeof sessionContent.videoUrl === 'object'
    ) {
      allVideos = Object.values(sessionContent.videoUrl).filter(
        url => typeof url === 'string',
      ) as string[];
    }

    // Only check R2 videos (not S3 videos)
    const r2Videos = allVideos.filter(item => {
      if (!item || typeof item !== 'string') return false;
      const isS3Url =
        item.includes('s3.amazonaws.com') ||
        item.includes('.s3.') ||
        /https?:\/\/[^/]+\.s3[^/]*\.amazonaws\.com/.test(item);
      return !isS3Url;
    });

    // Check each R2 video URL
    r2Videos.forEach(videoUrl => {
      if (
        videoUrl &&
        typeof videoUrl === 'string' &&
        !checkedVideos.has(videoUrl)
      ) {
        checkVideoExists(videoUrl);
      }
    });
  }, [sessionContent?.videoUrl, checkVideoExists, checkedVideos]);

  // No validation schema - all validation will be done manually on submit
  // This prevents any error messages from showing on the UI

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    reset,
    trigger,
    getValues,
    setError,
    clearErrors,
  } = useForm<IFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    shouldFocusError: false,
    defaultValues: {
      link: '',
      title: undefined,
      vocabularyContent: undefined,
      studentNote: undefined,
      grammarContent: undefined,
      homeworkContent: undefined,
      bookName: undefined,
      bookPages: undefined,
      bookUnits: undefined,
      bookLessons: undefined,
    },
    resolver: undefined,
  });

  useEffect(() => {
    if (!sessionContent) return;
    if (sessionContent.title && !isTriggerFile) {
      reset({
        title: sessionContent.title,
        vocabularyContent: sessionContent.vocabulary,
        studentNote: sessionContent.studentNote,
        grammarContent: sessionContent.grammar,
        homeworkContent: sessionContent.homework,
        bookName: sessionContent.bookName,
        bookLessons: sessionContent.bookLessons,
        bookPages: sessionContent.bookPages,
        bookUnits: sessionContent.bookUnits,
        link: sessionContent?.link,
      });
    }
  }, [sessionContent]);

  const onSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear all previous errors first
    clearErrors();
    setManualErrors({});

    // Get form values
    const formValues = getValues();

    // Manual validation for required fields
    const requiredFields: Array<{
      field: keyof IFormData;
      message: string;
    }> = [
      {
        field: 'title',
        message: intl.formatMessage({
          id: 'validation.required',
          defaultMessage: 'This field is required',
        }),
      },
      {
        field: 'vocabularyContent',
        message: intl.formatMessage({
          id: 'validation.required',
          defaultMessage: 'This field is required',
        }),
      },
      {
        field: 'grammarContent',
        message: intl.formatMessage({
          id: 'validation.required',
          defaultMessage: 'This field is required',
        }),
      },
      {
        field: 'studentNote',
        message: intl.formatMessage({
          id: 'validation.required',
          defaultMessage: 'This field is required',
        }),
      },
      {
        field: 'bookName',
        message: intl.formatMessage({
          id: 'validation.required',
          defaultMessage: 'This field is required',
        }),
      },
      {
        field: 'bookUnits',
        message: intl.formatMessage({
          id: 'validation.required',
          defaultMessage: 'This field is required',
        }),
      },
      {
        field: 'bookPages',
        message: intl.formatMessage({
          id: 'validation.required',
          defaultMessage: 'This field is required',
        }),
      },
      {
        field: 'bookLessons',
        message: intl.formatMessage({
          id: 'validation.required',
          defaultMessage: 'This field is required',
        }),
      },
      {
        field: 'homeworkContent',
        message: intl.formatMessage({
          id: 'validation.required',
          defaultMessage: 'This field is required',
        }),
      },
    ];

    // Check each required field and set error for missing ones
    const newErrors: Record<string, string> = {};
    let hasErrors = false;

    // Define minimum length requirements
    const minLengthRules: Record<string, number> = {
      title: 10,
      vocabularyContent: 20,
      grammarContent: 20,
      studentNote: 20,
      homeworkContent: 10,
    };

    for (const { field, message } of requiredFields) {
      const value = formValues[field];
      let isEmpty = false;

      if (value === undefined || value === null) {
        isEmpty = true;
      } else if (typeof value === 'string' && value.trim() === '') {
        isEmpty = true;
      } else if (field === 'bookPages') {
        // For bookPages, check if undefined/null/NaN, but allow 0
        if (
          value === undefined ||
          value === null ||
          (typeof value === 'number' && isNaN(value))
        ) {
          isEmpty = true;
        }
      }

      if (isEmpty) {
        newErrors[field] = message;
        hasErrors = true;
      } else if (typeof value === 'string' && minLengthRules[field]) {
        // Check minimum length for fields that have min length requirements
        const minLength = minLengthRules[field];
        if (value.trim().length < minLength) {
          newErrors[field] = intl.formatMessage(
            {
              id: 'validation.min_length',
              defaultMessage: 'Minimum {minLength} characters required',
            },
            { minLength },
          );
          hasErrors = true;
        }
      }
    }

    // If any required fields are missing, prevent submit
    if (hasErrors) {
      setManualErrors(newErrors);
      notification.error({
        title: intl.formatMessage({
          id: 'validation.error.title',
          defaultMessage: 'Validation Error',
        }),
        text: intl.formatMessage({
          id: 'validation.error.text',
          defaultMessage: 'Please check all fields and meet the requirements',
        }),
      });
      return;
    }

    // Transform undefined/null values to empty strings for backend compatibility
    const transformedValues: IFormData = {
      ...formValues,
      title: formValues.title || '',
      vocabularyContent: formValues.vocabularyContent || '',
      studentNote: formValues.studentNote || '',
      grammarContent: formValues.grammarContent || '',
      homeworkContent: formValues.homeworkContent || '',
      bookName: formValues.bookName || '',
      bookUnits: formValues.bookUnits || '',
      bookLessons: formValues.bookLessons || '',
      link: formValues.link || '',
      bookPages: formValues.bookPages || 0,
    };
    onSubmit(transformedValues, isDirty);
  };

  return (
    <Form
      id={ADD_SESSION_CONTENT_FORM_ID}
      component="form"
      noValidate
      onSubmit={onSubmitForm}
    >
      <FlexGroup>
        <FlexItem>
          <h1 className="font-bold text-xl">
            <FormattedMessage
              id="session_content.evidence"
              defaultMessage="1. Evidence"
            />
          </h1>
          <HorizontalRule className="mt-4" />

          <div>
            <FormRow
              fullWidth
              label={
                <FormattedMessage
                  id="session_content.recorded_video"
                  defaultMessage="Recorded video"
                />
              }
            >
              <FilePicker
                display="large"
                compressed
                initialPromptText={
                  <p>
                    <FormattedMessage
                      id="session_content.upload_video"
                      defaultMessage="Upload video"
                    />
                  </p>
                }
                fullWidth
                accept="video/mp4,video/x-msvideo,video/x-matroska,video/webm,video/3gpp,video/ogg,video/x-flv,.mp4,.avi,.mkv,.webm,.3gp,.ogv,.flv"
                onChange={(videos: FileList | null) =>
                  onSendHomeworkVideo(videos)
                }
                isLoading={isLoadingSendHomeworkVideo}
                disabled={view || isUploading}
                key={videoInputKey}
              />
            </FormRow>

            {/* Thêm Progress Bar */}
            {isUploading && (
              <div style={{ marginTop: '12px', marginBottom: '12px' }}>
                <FlexGroup direction="column" gutterSize="s">
                  <FlexItem>
                    <Text size="s" color="subdued">
                      <FormattedMessage
                        id="session_content.uploading_video"
                        defaultMessage="Uploading video... {progress}%"
                        values={{ progress: Math.round(uploadProgress) }}
                      />
                    </Text>
                  </FlexItem>
                  <FlexItem>
                    <Progress
                      value={uploadProgress}
                      max={100}
                      size="m"
                      color="primary"
                    />
                  </FlexItem>
                </FlexGroup>
              </div>
            )}

            <Text color="subdued" style={{ fontSize: '14px' }}>
              <p>
                <FormattedMessage
                  id="test_result.video_format_warning"
                  defaultMessage="The attachment video must be a file of type: mp4, avi, mkv, webm, 3gp, ogv, flv."
                />
              </p>
            </Text>

            {/* Warning about 24-hour retention policy */}
            <div
              style={{
                marginTop: '12px',
                padding: '12px',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '8px',
                marginBottom: '16px',
              }}
            >
              <Text
                style={{
                  fontSize: '13px',
                  color: '#856404',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <AlertTriangleIcon size={16} color="#ffc107" />
                <span>
                  <FormattedMessage
                    id="session_content.video_retention_warning"
                    defaultMessage="Video will be available on the system for 30 days after upload. After that, it will be automatically deleted according to the new policy."
                  />
                </span>
              </Text>
            </div>
          </div>

          {(() => {
            const videoUrls = sessionContent?.videoUrl;
            let allVideos: string[] = [];

            // Handle both array and object formats
            if (Array.isArray(videoUrls)) {
              allVideos = videoUrls || [];
            } else if (videoUrls && typeof videoUrls === 'object') {
              // Convert object to array of values
              allVideos = Object.values(videoUrls).filter(
                url => typeof url === 'string',
              ) as string[];
            }

            // Only show R2 videos (not S3 videos) for preview
            const r2Videos = allVideos.filter(item => {
              if (!item || typeof item !== 'string') return false;
              // Check if it's an S3 URL
              const isS3Url =
                item.includes('s3.amazonaws.com') ||
                item.includes('.s3.') ||
                /https?:\/\/[^/]+\.s3[^/]*\.amazonaws\.com/.test(item);
              // Return true for R2 videos (not S3)
              return !isS3Url;
            });
            return r2Videos;
          })().map((item, index) => {
            const isDeleted = deletedVideos.has(item);
            return (
              <div
                key={index}
                style={{ position: 'relative', marginBottom: '16px' }}
              >
                <FlexItem className="mb-2" style={{ position: 'relative' }}>
                  {isDeleted && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 77, 79, 0.12)',
                        border: '2px solid #ff4d4f',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10,
                        borderRadius: '8px',
                        padding: '20px 16px',
                        textAlign: 'center',
                        backdropFilter: 'blur(3px)',
                      }}
                    >
                      <ExclamationCircleOutlined
                        style={{
                          fontSize: '40px',
                          color: '#ff4d4f',
                          marginBottom: '10px',
                        }}
                      />
                      <div
                        style={{
                          color: '#ff4d4f',
                          fontSize: '15px',
                          fontWeight: 600,
                          marginBottom: '6px',
                        }}
                      >
                        <FormattedMessage
                          id="session_content.video_deleted"
                          defaultMessage="Video has been deleted"
                        />
                      </div>
                      <div
                        style={{
                          color: '#8c8c8c',
                          fontSize: '11px',
                          maxWidth: '260px',
                          lineHeight: '1.4',
                        }}
                      >
                        <FormattedMessage
                          id="session_content.video_deleted_description"
                          defaultMessage="This video was uploaded but has been deleted from R2 storage according to the new 24-hour retention policy."
                        />
                      </div>
                    </div>
                  )}
                  <video
                    controls
                    preload="auto"
                    playsInline
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      height: 'auto',
                      aspectRatio: '16/9',
                      opacity: isDeleted ? 0.35 : 1,
                      filter: isDeleted
                        ? 'grayscale(100%) brightness(0.65)'
                        : 'none',
                      borderRadius: '8px',
                      display: 'block',
                    }}
                    onError={e => {
                      console.error('Video playback error:', e);
                      const video = e.target as HTMLVideoElement;
                      const error = video.error;
                      if (error) {
                        console.error('Video error code:', error.code);
                        console.error('Video error message:', error.message);
                        console.error('Video URL:', item);

                        // Check if video is deleted (404 or network error)
                        // MEDIA_ERR_NETWORK can indicate 404 or network issues
                        // MEDIA_ERR_SRC_NOT_SUPPORTED can also indicate file not found
                        if (
                          error.code === error.MEDIA_ERR_NETWORK ||
                          error.code === error.MEDIA_ERR_SRC_NOT_SUPPORTED
                        ) {
                          // Mark video as deleted if it's an R2 URL (not S3)
                          if (
                            typeof item === 'string' &&
                            !item.includes('s3.amazonaws.com') &&
                            !item.includes('.s3.') &&
                            !/https?:\/\/[^/]+\.s3[^/]*\.amazonaws\.com/.test(
                              item,
                            )
                          ) {
                            // Double check with HEAD request if not already checked
                            if (!checkedVideos.has(item)) {
                              checkVideoExists(item);
                            } else {
                              // Already checked, mark as deleted
                              setDeletedVideos(prev => new Set(prev).add(item));
                            }
                          }
                        }

                        if (error.code === error.MEDIA_ERR_SRC_NOT_SUPPORTED) {
                          console.error(
                            'Video format not supported or file corrupted',
                          );
                        } else if (error.code === error.MEDIA_ERR_NETWORK) {
                          console.error(
                            'Network error loading video - 416 Range Not Satisfiable',
                          );
                          // Try to reload without range requests
                          setTimeout(() => {
                            video.load();
                          }, 1000);
                        } else if (error.code === error.MEDIA_ERR_DECODE) {
                          console.error('Error decoding video');
                        }
                      }
                    }}
                    onLoadStart={() => {}}
                    onCanPlay={() => {}}
                    onLoadedMetadata={() => {}}
                  >
                    <source src={item} type="video/webm" />
                    <source src={item} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </FlexItem>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: '8px',
                    width: '100%',
                  }}
                >
                  <ButtonEmpty
                    isLoading={isLoadingDeleteVideo}
                    onClick={() => {
                      // Pass the video URL directly instead of index
                      if (item && typeof item === 'string') {
                        onDeleteVideo(item);
                      }
                    }}
                  >
                    <FlexGroup
                      gutterSize="s"
                      style={{ alignItems: 'center', color: '#ff4d4f' }}
                    >
                      <FlexItem>
                        <XIcon
                          style={{
                            width: '16px',
                            height: '16px',
                            color: '#ff4d4f',
                          }}
                        />
                      </FlexItem>
                      <FlexItem style={{ color: '#ff4d4f' }}>
                        <FormattedMessage
                          id="common.remove"
                          defaultMessage="Remove"
                        />
                      </FlexItem>
                    </FlexGroup>
                  </ButtonEmpty>
                </div>
              </div>
            );
          })}

          <FormRow
            fullWidth
            label={
              <FormattedMessage
                id="session_content.link_recorded_video"
                defaultMessage="Link to recorded video"
              />
            }
            isInvalid={!!errors.link}
            error={errors.link?.message}
            helpText={
              <Text size="xs" color="subdued">
                <FormattedMessage
                  id="session_content.link_recorded_video.help"
                  defaultMessage="Optional - External video link (YouTube, Drive, etc.)"
                />
              </Text>
            }
          >
            <Controller
              name="link"
              control={control}
              render={({ field: { ref, ...fieldRest } }) => (
                <FieldText
                  inputRef={ref}
                  {...fieldRest}
                  isInvalid={!!errors.link}
                  fullWidth
                  placeholder={intl.formatMessage({
                    id: 'session_content.link_recorded_video.placeholder',
                    defaultMessage: 'E.g: https://youtube.com/watch?v=...',
                  })}
                  disabled={view}
                />
              )}
            />
          </FormRow>
        </FlexItem>
      </FlexGroup>

      <Spacer />
      <Spacer />

      <h1 className="font-bold text-xl ">
        <FormattedMessage
          id="session_content.add_content"
          defaultMessage="2. Add content"
        />
      </h1>
      <HorizontalRule className="mt-4" />

      <FlexGroup>
        <FlexItem>
          <FormRow
            fullWidth
            label={
              <>
                <FormattedMessage
                  id="session_content.title"
                  defaultMessage="Title"
                />{' '}
                <span style={{ color: 'red' }}>*</span>
              </>
            }
            isInvalid={!!manualErrors.title}
            error={manualErrors.title}
            helpText={
              !manualErrors.title && (
                <Text size="xs" color="subdued">
                  <FormattedMessage
                    id="validation.min_length"
                    values={{ minLength: 10 }}
                    defaultMessage="Minimum {minLength} characters required"
                  />
                </Text>
              )
            }
          >
            <Controller
              name="title"
              control={control}
              render={({ field: { ref, ...fieldRest } }) => (
                <FieldText
                  inputRef={ref}
                  {...fieldRest}
                  isInvalid={!!manualErrors.title}
                  fullWidth
                  placeholder={intl.formatMessage({
                    id: 'session_content.title.placeholder',
                    defaultMessage: 'Enter lesson title (min. 10 characters)',
                  })}
                  disabled={view}
                />
              )}
            />
          </FormRow>
        </FlexItem>
      </FlexGroup>

      <FlexGroup>
        <FlexItem>
          <FormRow
            fullWidth
            label={
              <>
                <FormattedMessage
                  id="session_content.vocabulary"
                  defaultMessage="Vocabulary"
                />{' '}
                <span style={{ color: 'red' }}>*</span>
              </>
            }
            isInvalid={!!manualErrors.vocabularyContent}
            error={manualErrors.vocabularyContent}
            helpText={
              !manualErrors.vocabularyContent && (
                <Text size="xs" color="subdued">
                  <FormattedMessage
                    id="session_content.vocabulary.help"
                    defaultMessage="Minimum 20 characters - List vocabulary words taught"
                  />
                </Text>
              )
            }
          >
            <Controller
              name="vocabularyContent"
              control={control}
              render={({ field: { ref, ...fieldRest } }) => (
                <TextArea
                  className="rounded-lg"
                  rows={2}
                  resize="none"
                  inputRef={ref}
                  {...fieldRest}
                  isInvalid={!!manualErrors.vocabularyContent}
                  fullWidth
                  placeholder={intl.formatMessage({
                    id: 'session_content.vocabulary.placeholder',
                    defaultMessage: 'E.g: apple, banana, orange, watermelon...',
                  })}
                  disabled={view}
                />
              )}
            />
          </FormRow>
        </FlexItem>
      </FlexGroup>

      <FlexGroup>
        <FlexItem>
          <FormRow
            fullWidth
            isInvalid={!!manualErrors.grammarContent}
            error={manualErrors.grammarContent}
            helpText={
              !manualErrors.grammarContent && (
                <Text size="xs" color="subdued">
                  <FormattedMessage
                    id="session_content.grammar.help"
                    defaultMessage="Minimum 20 characters - Grammar points practiced"
                  />
                </Text>
              )
            }
            label={
              <>
                <FormattedMessage
                  id="session_content.grammar"
                  defaultMessage="Grammar"
                />{' '}
                <span style={{ color: 'red' }}>*</span>
              </>
            }
          >
            <Controller
              name="grammarContent"
              control={control}
              render={({ field: { ref, value, ...fieldRest } }) => (
                <TextArea
                  rows={2}
                  resize="none"
                  placeholder={intl.formatMessage({
                    id: 'session_content.grammar.placeholder',
                    defaultMessage:
                      'E.g: Practice the names: Shanghai, Puerto Rico, Seoul, Athens...',
                  })}
                  fullWidth
                  inputRef={ref}
                  {...fieldRest}
                  isInvalid={!!manualErrors.grammarContent}
                  disabled={view}
                />
              )}
            />
          </FormRow>
        </FlexItem>
      </FlexGroup>

      <FlexGroup>
        <FlexItem>
          <FormRow
            fullWidth
            label={
              <>
                <FormattedMessage
                  id="session_content.note_students"
                  defaultMessage="Note for students"
                />{' '}
                <span style={{ color: 'red' }}>*</span>
              </>
            }
            isInvalid={!!manualErrors.studentNote}
            error={manualErrors.studentNote}
            helpText={
              !manualErrors.studentNote && (
                <Text size="xs" color="subdued">
                  <FormattedMessage
                    id="session_content.note_students.help"
                    defaultMessage="Minimum 20 characters - Student's performance & feedback"
                  />
                </Text>
              )
            }
          >
            <Controller
              name="studentNote"
              control={control}
              render={({ field: { ref, value, ...fieldRest } }) => (
                <TextArea
                  rows={2}
                  resize="none"
                  placeholder={intl.formatMessage({
                    id: 'session_content.note_students.placeholder',
                    defaultMessage:
                      'The student participated well in class, followed the instruction...',
                  })}
                  inputRef={ref}
                  {...fieldRest}
                  isInvalid={!!manualErrors.studentNote}
                  fullWidth
                  disabled={view}
                />
              )}
            />
          </FormRow>
        </FlexItem>
      </FlexGroup>

      <Spacer />
      <Spacer />

      <h1 className="font-bold text-xl">
        <FormattedMessage
          id="session_content.courses_book"
          defaultMessage="3. Course's book"
        />
      </h1>

      <HorizontalRule className="mt-4" />

      <FlexGroup>
        <FlexItem>
          <FormRow
            fullWidth
            label={
              <>
                <FormattedMessage
                  id="session_content.book_name"
                  defaultMessage="Name of the book"
                />{' '}
                <span style={{ color: 'red' }}>*</span>
              </>
            }
            isInvalid={!!manualErrors.bookName}
            error={manualErrors.bookName}
            helpText={
              !manualErrors.bookName && (
                <Text size="xs" color="subdued">
                  <FormattedMessage
                    id="session_content.book_name.help"
                    defaultMessage="Textbook or course material used"
                  />
                </Text>
              )
            }
          >
            <Controller
              name="bookName"
              control={control}
              render={({ field: { ref, ...fieldRest } }) => (
                <FieldText
                  inputRef={ref}
                  {...fieldRest}
                  fullWidth
                  placeholder={intl.formatMessage({
                    id: 'session_content.book_name.placeholder',
                    defaultMessage:
                      'E.g: Side by Side 1, English File Elementary...',
                  })}
                  isInvalid={!!manualErrors.bookName}
                  disabled={view}
                />
              )}
            />
          </FormRow>
        </FlexItem>
      </FlexGroup>

      <FlexGroup>
        <FlexItem>
          <FormRow
            fullWidth
            label={
              <>
                <FormattedMessage
                  id="session_content.unit"
                  defaultMessage="Unit"
                />{' '}
                <span style={{ color: 'red' }}>*</span>
              </>
            }
            isInvalid={!!manualErrors.bookUnits}
            error={manualErrors.bookUnits}
          >
            <Controller
              name="bookUnits"
              control={control}
              render={({ field: { ref, ...fieldRest } }) => (
                <FieldText
                  inputRef={ref}
                  {...fieldRest}
                  isInvalid={!!manualErrors.bookUnits}
                  fullWidth
                  placeholder={intl.formatMessage({
                    id: 'session_content.unit.placeholder',
                    defaultMessage: 'E.g: Unit 1, Unit 5, Chapter 3...',
                  })}
                  disabled={view}
                />
              )}
            />
          </FormRow>
        </FlexItem>
      </FlexGroup>

      <FlexGroup>
        <FlexItem>
          <FormRow
            fullWidth
            label={
              <>
                <FormattedMessage
                  id="session_content.page"
                  defaultMessage="Page"
                />{' '}
                <span style={{ color: 'red' }}>*</span>
              </>
            }
            isInvalid={!!manualErrors.bookPages}
            error={manualErrors.bookPages}
            helpText={
              !manualErrors.bookPages && (
                <Text size="xs" color="subdued">
                  <FormattedMessage
                    id="session_content.page.help"
                    defaultMessage="Page number (e.g: 2, 15, 42)"
                  />
                </Text>
              )
            }
          >
            <Controller
              name="bookPages"
              control={control}
              render={({ field: { ref, ...fieldRest } }) => (
                <FieldNumber
                  className="rounded-lg"
                  inputRef={ref}
                  {...fieldRest}
                  min={1}
                  isInvalid={!!manualErrors.bookPages}
                  fullWidth
                  placeholder={intl.formatMessage({
                    id: 'session_content.page.placeholder',
                    defaultMessage: 'E.g: 2',
                  })}
                  disabled={view}
                />
              )}
            />
          </FormRow>
        </FlexItem>
      </FlexGroup>

      <FlexGroup>
        <FlexItem>
          <FormRow
            fullWidth
            label={
              <>
                <FormattedMessage
                  id="session_content.lesson"
                  defaultMessage="Lesson"
                />{' '}
                <span style={{ color: 'red' }}>*</span>
              </>
            }
            isInvalid={!!manualErrors.bookLessons}
            error={manualErrors.bookLessons}
          >
            <Controller
              render={({ field: { ref, ...fieldRest } }) => (
                <TextArea
                  rows={2}
                  placeholder={intl.formatMessage({
                    id: 'session_content.lesson.placeholder',
                    defaultMessage:
                      'E.g: Lesson 1 - Greetings, Lesson 5A - Daily routines...',
                  })}
                  resize="none"
                  fullWidth
                  inputRef={ref}
                  isInvalid={!!manualErrors.bookLessons}
                  {...fieldRest}
                  disabled={view}
                />
              )}
              name="bookLessons"
              control={control}
            />
          </FormRow>
        </FlexItem>
      </FlexGroup>

      <Spacer />
      <Spacer />

      <h1 className="font-bold text-xl">
        <FormattedMessage
          id="session_content.homework"
          defaultMessage="4. Homework"
        />
      </h1>
      <HorizontalRule className="mt-4" />

      <FlexGroup>
        <FlexItem>
          <FormRow
            fullWidth
            label={
              <>
                <FormattedMessage
                  id="session_content.content"
                  defaultMessage="Content"
                />{' '}
                <span style={{ color: 'red' }}>*</span>
              </>
            }
            isInvalid={!!manualErrors.homeworkContent}
            error={manualErrors.homeworkContent}
            helpText={
              !manualErrors.homeworkContent && (
                <Text size="xs" color="subdued">
                  <FormattedMessage
                    id="session_content.content.help"
                    defaultMessage="Minimum 10 characters - Homework assignment details"
                  />
                </Text>
              )
            }
          >
            <Controller
              name="homeworkContent"
              control={control}
              render={({ field: { ref, ...fieldRest } }) => (
                <TextArea
                  rows={2}
                  placeholder={intl.formatMessage({
                    id: 'session_content.content.placeholder',
                    defaultMessage:
                      'E.g: Complete exercises 1-5 on page 12, Practice new vocabulary...',
                  })}
                  resize="none"
                  isInvalid={!!manualErrors.homeworkContent}
                  fullWidth
                  inputRef={ref}
                  {...fieldRest}
                  disabled={view}
                />
              )}
            />
          </FormRow>
        </FlexItem>
      </FlexGroup>

      <FlexGroup direction="column">
        <FlexItem>
          <FormRow
            fullWidth
            label={
              <FormattedMessage
                id="session_content.attachment"
                defaultMessage="Attachment"
              />
            }
          >
            <div style={{ position: 'relative' }}>
              <FilePicker
                display="large"
                compressed
                initialPromptText={
                  <p>
                    <FormattedMessage
                      id="session_content.upload_generate_homework"
                      defaultMessage="Upload & Generate Homework"
                    />
                  </p>
                }
                fullWidth
                onChange={(files: FileList | null) => onSendHomework(files)}
                isLoading={isLoadingSendHomeworkFile}
                disabled={view}
                key={homeworkInputKey}
              />
            </div>
          </FormRow>
          <Text color="subdued" style={{ fontSize: '14px' }}>
            <p>Max size: 10 MB (Type: pdf, jpg, png, word, ppt)</p>
          </Text>
        </FlexItem>

        <FlexItem>
          <DownloadList
            isDisable={false}
            deleteHandler={onDeleteFile}
            data={sessionContent?.homeworkUrl ?? []}
          />
        </FlexItem>
      </FlexGroup>

      <div className="w-full flex">
        <Button
          fill
          type="submit"
          form={ADD_SESSION_CONTENT_FORM_ID}
          isLoading={isLoading}
          disabled={view}
          className="ml-auto"
        >
          <FormattedMessage id="common.save" defaultMessage="Save" />
        </Button>
      </div>
    </Form>
  );
};

export default memo(SessionContentForm);
